import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import type { TransactionVersion } from "@solana/web3.js";
import type { FC } from "react";
import { createContext, useCallback, useContext } from "react";

// TODO: Manage with external dependency
import { useLocalStorageState } from "../../utils/utils";

const DEFAULT_SLIPPAGE = 0.005;
export const DUST_THRESHOLD = 0.99;
interface AppConfigContextState {
  network: WalletAdapterNetwork;
  setNetwork: (_val: WalletAdapterNetwork) => void;
  slippage: number;
  setSlippage: (val: number) => void;
  hideDust: boolean;
  setHideDust: (val: boolean) => void;
  hideUnknownTokens: boolean;
  setHideUnknownTokens: (val: boolean) => void;
  hideCompressedNFTs: boolean;
  setHideCompressedNFTs: (val: boolean) => void;
  valuesHidden: boolean;
  setValuesHidden: (val: any) => void;
  hideableValueWrapper: (value: any) => any;
  transactionVersion: TransactionVersion;
  setTransactionVersion: (val: TransactionVersion) => void;
}

const AppConfigContext = createContext<AppConfigContextState>({
  network: WalletAdapterNetwork.Mainnet,
  setNetwork: (_val: WalletAdapterNetwork) => {},
  slippage: DEFAULT_SLIPPAGE,
  setSlippage: (_val: number) => {},
  hideDust: true,
  setHideDust: (_val: boolean) => {},
  hideUnknownTokens: true,
  setHideUnknownTokens: (_val: boolean) => {},
  hideCompressedNFTs: true,
  setHideCompressedNFTs: (_val: boolean) => {},
  valuesHidden: false,
  setValuesHidden: (_val: boolean) => {},
  hideableValueWrapper: (_val: any) => {},
  transactionVersion: 0,
  setTransactionVersion: (_val: TransactionVersion) => {},
});

export const AppConfigProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [slippage, setSlippage] = useLocalStorageState("slippage", DEFAULT_SLIPPAGE.toString());
  const [transactionVersion, setTransactionVersion] = useLocalStorageState("transactionVersion", 0);

  const [hideDust, setHideDust] = useLocalStorageState("hideSmallBalances", "true");
  const [hideUnknownTokens, setHideUnknownTokens] = useLocalStorageState("hideUnknownTokens", "true");

  const [hideCompressedNFTs, setHideCompressedNFTs] = useLocalStorageState("hideCompressedNFTs", "false");

  const [network, setNetwork] = useLocalStorageState("network", WalletAdapterNetwork.Mainnet);

  const [valuesHidden, setValuesHidden] = useLocalStorageState("valuesHidden", false);

  const hideableValueWrapper = useCallback((value) => (valuesHidden ? "******" : value), [valuesHidden]);

  return (
    <AppConfigContext.Provider
      value={{
        network,
        setNetwork: (val) => setNetwork(val),
        slippage: parseFloat(slippage),
        setSlippage: (val) => setSlippage(val.toString()),
        hideDust: hideDust === "true",
        setHideDust: (val) => setHideDust(val.toString()),
        hideUnknownTokens: hideUnknownTokens === "true",
        setHideUnknownTokens: (val) => setHideUnknownTokens(val.toString()),
        hideCompressedNFTs: hideCompressedNFTs === "true",
        setHideCompressedNFTs: (val) => setHideCompressedNFTs(val.toString()),
        valuesHidden,
        setValuesHidden,
        hideableValueWrapper,
        transactionVersion,
        setTransactionVersion,
      }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => useContext(AppConfigContext);

export const useSlippageConfig = () => {
  const { slippage, setSlippage } = useAppConfig();
  return { slippage, setSlippage };
};

export const useHideDustConfig = () => {
  const { hideDust, setHideDust } = useAppConfig();
  return { hideDust, setHideDust };
};

export const useUnknownTokenConfig = () => {
  const { hideUnknownTokens, setHideUnknownTokens } = useAppConfig();
  return { hideUnknownTokens, setHideUnknownTokens };
};

export const useNetworkConfig = () => {
  const { network, setNetwork } = useAppConfig();
  return { network, setNetwork };
};
